import cx from 'classnames';
import Image from 'next/image';
import { useIntl } from 'react-intl';

import { IllustrationTravelerPledge } from '../../../app/account/trips/shared/components/IllustrationTravelerPledge';
import { useTrackEventAmplitude } from '../../../core/Tracking/Amplitude/Amplitude';
import { Button } from '../../../design-system/components/form/button/Button';
import { IconStarHighlight } from '../../../design-system/components/icons/StarHighlight';
import { List, ListItem } from '../../../design-system/components/list/List';
import { Typography } from '../../../design-system/components/typography/Typography';
import { HtmlContent } from '../../../domains/Layout/HtmlContent/HtmlContent';
import { imageLoaderForId } from '../../../domains/Layout/Image/URL/ImageURL';
import { Destination } from '../../../resources/destination/domain/types/destination';
import { IntrinsicElement } from '../../utilities/types/IntrinsicElement';
import { PickDomain } from '../../utilities/types/PickDomain';

import style from './WhenToGoSection.module.css';

type WhenToGoSectionProps = {
    title: string;
    content: string;
    cta: {
        label: string;
        url: string;
        title: string;
    };
    destination: PickDomain<Destination, 'id' | 'slug'>;
    subTitleElement?: IntrinsicElement;
};

export function WhenToGoSection({
    title,
    content,
    cta,
    destination,
    subTitleElement,
}: WhenToGoSectionProps) {
    const { formatMessage } = useIntl();
    const { trackEvent } = useTrackEventAmplitude();

    const overviewWhenToGoItems = [
        formatMessage({ id: 'global.overviewWhenToGo.texte1' }),
        formatMessage({ id: 'global.overviewWhenToGo.texte2' }),
        formatMessage({ id: 'global.overviewWhenToGo.texte3' }),
    ];

    return (
        <div className={style.container}>
            <Image
                src="1824386"
                alt={formatMessage({ id: 'page.destination.whenToGo.title' })}
                className="imageCover"
                fill
                loader={imageLoaderForId({
                    aspectRatio: 16 / 9,
                })}
            />

            <div className={cx(style.mainContainer)}>
                <div className={style.textContent}>
                    <Typography scale="secondary-m-bold">
                        {formatMessage({ id: 'page.destination.whenToGo.title' })}
                    </Typography>

                    <div className={style.description}>
                        <Typography as={subTitleElement} scale="primary-s-extra-bold">
                            {title}
                        </Typography>

                        <HtmlContent htmlContent={content} />

                        <div>
                            <Button
                                priority="secondary-filled"
                                href={cta.url}
                                title={cta.title}
                                onClick={() => {
                                    trackEvent((ampli, defaultProperties) => {
                                        ampli.whentogoClicked({
                                            ...defaultProperties,
                                            block: 'whentogo',
                                            destination_id: destination.id,
                                            destination_slug: destination.slug,
                                        });
                                    });
                                }}
                            >
                                {formatMessage({ id: 'global.cta.seeWhenToGo' })}
                            </Button>
                        </div>
                    </div>
                </div>
                <div className={style.imageContent}>
                    <div className={style.imageWrapper}>
                        <Image
                            src="1824387"
                            alt={formatMessage({ id: 'page.destination.whenToGo.title' })}
                            className="imageCover"
                            fill
                            loader={imageLoaderForId({
                                aspectRatio: 16 / 9,
                            })}
                        />
                    </div>
                    <div className={style.textOverlay}>
                        <div>
                            <IllustrationTravelerPledge />
                        </div>

                        <List BulletIcon={IconStarHighlight} classes={{ marker: style.listMarker }}>
                            {overviewWhenToGoItems.map((item, index) => (
                                <ListItem key={index}>
                                    <strong>{item}</strong>
                                </ListItem>
                            ))}
                        </List>
                    </div>
                </div>
            </div>
        </div>
    );
}
